import React, { useMemo, useCallback } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Flex, Text, Icon
} from 'components/ui';
import RewardsCarousel from 'components/Content/BeautyInsider/RewardsCarousel';
import BiRewardButton from 'components/Content/BeautyInsider/BiRewardButton';
import Chiclet from 'components/Chiclet';
import { mediaQueries, space } from 'style/config';
import localeUtils from 'utils/LanguageLocale';
import rougeExclusiveUtils from 'utils/rougeExclusive';
import UI from 'utils/UI';
import anaConstants from 'analytics/constants';
import BeautyInsiderAllRewardsEN from './locales/BeautyInsiderAllRewards_en_US';
import BeautyInsiderAllRewardsFR from './locales/BeautyInsiderAllRewards_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(BeautyInsiderAllRewardsEN, BeautyInsiderAllRewardsFR);

const styles = {
    listVariant: {
        container: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexWrap: 'nowrap',
            [mediaQueries.smMax]: {
                justifyContent: 'space-between'
            }
        },
        list: {
            display: 'flex',
            flexWrap: 'nowrap',
            overflowX: 'auto',
            padding: 0,
            margin: `0 -${space[4]}px`,
            paddingLeft: `${space[2]}px`,
            listStyleType: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
                display: 'none'
            },
            [mediaQueries.sm]: {
                paddingLeft: `${space[4]}px`
            }
        },
        chiclet: {
            padding: '4px 0',
            margin: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
            overflow: 'visible',
            textOverflow: 'clip',
            flexShrink: 0,
            flexGrow: 0
        }
    }
};

const BeautyInsiderAllRewards = props => {
    const { biRewardGroups, basket, isAnonymous, localization } = props;

    // Check if threshold qualifies for free shipping (750+ points)
    const isFreeShippingEligible = useCallback(threshold => {
        const freeShippingTiers = ['750-2999 Points'];

        return freeShippingTiers.includes(threshold);
    }, []);

    // Process biRewardGroups into grids array
    const biRewardsGrids = useMemo(() => {
        if (!biRewardGroups || Object.keys(biRewardGroups).length === 0) {
            return [];
        }

        const rewardsGridsNames = [
            '50 Points',
            '100 Points',
            '250-499 Points',
            '500-749 Points',
            '750-2999 Points',
            '3000-4999 Points',
            '5000-19999 Points',
            '20000+ Points'
        ];

        const updatedBiRewardGroups = rougeExclusiveUtils.updateRougeExclusiveBiRewardGroups(biRewardGroups, basket);
        const grids = [];

        Object.keys(updatedBiRewardGroups).forEach(key => {
            if (rewardsGridsNames.indexOf(key) >= 0 && updatedBiRewardGroups[key].length > 0) {
                grids.push({
                    items: updatedBiRewardGroups[key].map(item => {
                        return { ...item, threshold: key };
                    }),
                    anchor: key.replace(/\s/g, '').toLowerCase(),
                    title: getText(key),
                    isAnonymous: isAnonymous,
                    isBIRBReward: true
                });
            }
        });

        return grids;
    }, [biRewardGroups, basket, isAnonymous]);

    // Render jump links for navigation
    const renderJumpLinks = useCallback(grids => {
        const clickHandler = (e, scrollTo) => {
            e.preventDefault();
            UI.scrollTo({ elementId: scrollTo });
        };

        const jumplinksTitle = input => input.replace(/(\d+)(?:-(\d+))?points/i, (match, p1, p2) => (p2 ? `${p1}+ points` : `${p1} points`));

        return (
            <div css={styles.listVariant.container}>
                <ul css={styles.listVariant.list}>
                    {grids.map((grid, index) => {
                        const title = jumplinksTitle(grid.anchor);

                        return (
                            <li
                                key={`jumplinks-${index.toString()}`}
                                css={styles.listVariant.chiclet}
                            >
                                <Chiclet
                                    onClick={e => clickHandler(e, grid.anchor)}
                                    children={title}
                                    variant='shadow'
                                    marginRight={2}
                                    minWidth={6}
                                />
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    }, []);

    const renderBiButton = useCallback(
        ({ analyticsContext, sku }) => (
            <BiRewardButton
                sku={sku}
                analyticsContext={analyticsContext}
                isAnonymous={isAnonymous}
                basket={basket}
            />
        ),
        [isAnonymous, basket]
    );

    // Don't render if no grids
    if (!biRewardsGrids || biRewardsGrids.length === 0) {
        return null;
    }

    return (
        <Box marginTop={[6, null, 7]}>
            <Flex
                id='all-rewards'
                flexDirection='column'
                marginBottom={5}
            >
                <Text
                    is='h2'
                    fontWeight='bold'
                    fontSize={['md', 'xl-bg']}
                    marginBottom={3}
                >
                    {localization.allRewards}
                </Text>
                {renderJumpLinks(biRewardsGrids)}
            </Flex>
            <Flex flexDirection='column'>
                {biRewardsGrids.map((grid, index) => {
                    const threshold = grid.items[0]?.threshold;
                    const showFreeShipping = threshold && isFreeShippingEligible(threshold);

                    return (
                        <React.Fragment key={`Rewards-grid-${index.toString()}`}>
                            <Box
                                id={grid.anchor}
                                marginBottom={[6, null, 6]}
                            >
                                <Text
                                    is='h3'
                                    fontWeight='bold'
                                    fontSize={['md', 'xl-bg']}
                                    marginBottom={4}
                                >
                                    {grid.title}
                                </Text>
                                {showFreeShipping && (
                                    <Flex
                                        alignItems='flex-start'
                                        marginBottom={4}
                                        paddingY={3}
                                        paddingLeft={4}
                                        paddingRight={5}
                                        backgroundColor='rgb(248, 248, 248)'
                                        borderRadius='4px'
                                    >
                                        <Icon
                                            name='truck'
                                            size='lg'
                                            marginRight={2}
                                        />
                                        <Text
                                            fontSize='md'
                                            fontWeight='regular'
                                        >
                                            {localization.freeShipping}
                                        </Text>
                                    </Flex>
                                )}
                                <RewardsCarousel
                                    items={grid.items}
                                    title={grid.title}
                                    showPrice={true}
                                    showMarketingFlags={true}
                                    size='small'
                                    rougeBadgeText={getText('rougeBadge')}
                                    analyticsContext={anaConstants.CONTEXT.BASKET_REWARDS}
                                    renderBiButton={renderBiButton}
                                    hideVariationText={true}
                                />
                            </Box>
                        </React.Fragment>
                    );
                })}
            </Flex>
        </Box>
    );
};

export default wrapFunctionalComponent(BeautyInsiderAllRewards, 'BeautyInsiderAllRewards');
